import { useLazyQuery } from '@apollo/client';
import { useMemo } from 'react';
import { isDocumentTemplateEntityType, toDocumentDataInput } from 'src/lib/documentDataInput';
import { DocumentDataApi } from 'src/modules/document-preview';
import type { GetDocumentData, GetDocumentDataVariables } from 'types/graphql';

type UseDocumentDataOptions = {
  entityType?: string | null;
  /** The record (`{ slug }`, `{ id }`, …) or report parameters; anything that is not an object sends nothing. */
  variables?: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export default function useDocumentData({ entityType, variables }: UseDocumentDataOptions) {
  const [fetchGenericDocument] = useLazyQuery<GetDocumentData, GetDocumentDataVariables>(
    DocumentDataApi.GetDocumentData
  );

  return useMemo(
    () => ({
      getDocumentData: () => {
        // An entity type outside the SDL enum was rejected by GraphQL validation and resolved to null.
        if (!entityType || !isRecord(variables) || !isDocumentTemplateEntityType(entityType)) return null;

        return fetchGenericDocument({ variables: { input: toDocumentDataInput(entityType, variables) } }).then(
          (res) => res.data?.documentData ?? null
        );
      },
    }),
    [entityType, fetchGenericDocument, variables]
  );
}
